import { useRef } from "react";

/**
 * True once `current` has changed from its value on this hook's first
 * render (captured once into `initialJsonRef`, below). Feeds Dialog.tsx's
 * `isDirty` prop.
 *
 * Compares via JSON.stringify rather than a deep-equal library: every
 * caller's state here is plain data with no functions/Date/Map inside, so
 * this is cheap enough and needs no new dependency. Only `current` is
 * re-stringified on every render, though — the INITIAL snapshot is
 * stringified exactly once (guarded by the `null` check below) and cached,
 * not redundantly re-stringified every render alongside it (its own value
 * never changes after the first render, so re-deriving the same string from
 * it repeatedly was pure waste).
 */
export function useIsDirty<T>(current: T): boolean {
  const initialJsonRef = useRef<string | null>(null);
  if (initialJsonRef.current === null) initialJsonRef.current = JSON.stringify(current);
  return JSON.stringify(current) !== initialJsonRef.current;
}
